import React, { createContext, PropsWithChildren, useCallback, useEffect, useReducer } from 'react';

import { getWindow } from '../services/windowService';

const safeParse = (rawValue?: string | null) => {
  if (rawValue === null || typeof rawValue === 'undefined') {
    return undefined;
  }
  try {
    let result = JSON.parse(rawValue);
    if (typeof result === 'number') {
      result = String(result);
    }
    return result;
  } catch {
    return rawValue;
  }
};

const wnd = getWindow();
const { sessionStorage } = wnd;

type State = Record<string, any>;

enum Actions {
  SET_ITEM = 'SET_ITEM',
  REMOVE_ITEM = 'REMOVE_ITEM',
  CLEAR = 'CLEAR',
}

const setItem = (state: State, payload: any) => {
  return {
    ...state,
    ...payload,
  };
};
const removeItem = (state: State, payload: string) => {
  const next = { ...state };
  delete next[payload];
  return next;
};

const clear = () => {
  return {};
};

const syncWithLocalStorage = () => {
  const ss = Object.entries<string>(sessionStorage).reduce<Record<string, string>>((acc, pair) => {
    acc[pair[0]] = safeParse(pair[1]);
    return acc;
  }, {});
  return ss;
};

type Action =
  | { type: Actions.SET_ITEM; payload: any }
  | { type: Actions.CLEAR; payload?: never }
  | { type: Actions.REMOVE_ITEM; payload: string };

const reducer = (state: State, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case Actions.SET_ITEM:
      state = setItem(state, payload);
      break;
    case Actions.CLEAR:
      state = clear();
      break;
    case Actions.REMOVE_ITEM:
      state = removeItem(state, payload);
      break;
    default:
      console.error(`Action ${(action as any).type} is not implemented.`);
      break;
  }
  return state;
};

export interface SessionStorageContextProps {
  setValue: (key: string) => <T>(value: T) => void;
  removeValue: (key: string) => () => void;
  state: Record<string, any>;
}

export const SessionStorageContext = createContext<SessionStorageContextProps | null>(null);

export const SessionStorageContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, syncWithLocalStorage());

  useEffect(() => {
    const storageListener = (e: StorageEvent) => {
      const { key, newValue } = e;
      if (key === null) {
        dispatch({ type: Actions.CLEAR });
        return;
      }
      dispatch({
        type: Actions.SET_ITEM,
        payload: { [key]: safeParse(newValue) },
      });
    };

    wnd.addEventListener('storage', storageListener);
    return () => {
      wnd.removeEventListener('storage', storageListener);
    };
  }, []);

  const setValue = useCallback((key: string) => {
    return <T,>(value: T) => {
      try {
        const valueToInsert = typeof value === 'object' ? JSON.stringify(value) : String(value);
        sessionStorage.setItem(key, valueToInsert);
        dispatch({ type: Actions.SET_ITEM, payload: { [key]: value } });
      } catch (error) {
        console.error(error);
      }
    };
  }, []);

  const removeValue = useCallback(
    (key: string) => () => {
      sessionStorage.removeItem(key);
      dispatch({ type: Actions.REMOVE_ITEM, payload: key });
    },
    [],
  );

  return (
    <SessionStorageContext.Provider value={{ setValue, removeValue, state }}>
      {children}
    </SessionStorageContext.Provider>
  );
};
